import * as IoC from 'typescript-ioc';
import { FormFieldProps, InitialData } from '../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../infrastructure/implementations/form-viewmodel-controller';
import * as mobx from 'mobx';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import { OrderBookingEventBlueprintFieldKey, orderBookingEventBlueprintFormConfigList } from './order-booking-event-blueprint.config';
import { IntlShape } from 'react-intl';
import { IEventBlueprintDTO } from '../../../../api/blueprints/event.blueprint.types';
import { TObject } from '../../../../infrastructure/type-extensions';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../infrastructure/utils/blueprint-details.mapper';
import { IBlueprintItemDetails } from '../../../../api/blueprints/blueprint-details.types';
import { GlobalViewModel } from '../../../global.viewmodel';
import { handleApiException } from '../../../global/modal/alerts-host/alert-templates';
import { IBlueprintsDAO } from '../../../../api/blueprints/blueprints.dao';
import { IBlueprintDTO } from '../../../../api/blueprints/blueprint.types';
import { IAttributesDAO, IEventAttributeDetailsDTO, TGetEventAttributesSortKeys } from '../../../../api/attributes.dao';
import { ISortRequestParams, SortOrder } from '../../../../api/shared.dto';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';

export class OrderBookingEventBlueprintsFormVM {

    private static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'OrderBookingEventBlueprintsFormVM', orderBookingEventBlueprintFormConfigList);
            }
        }();
    }

    @mobx.observable
    public loading = false;
    @mobx.observable
    public currentBlueprint: IEventBlueprintDTO;
    @mobx.observable
    private formDataCtrl: AbstractFormViewModelController;
    @mobx.observable
    private saving = false;
    @mobx.observable
    private eventAttributes: IEventAttributeDetailsDTO[] = [];

    constructor(
        public readonly blueprint: IEventBlueprintDTO,
        public readonly accountId: number,
        private readonly formSubmitAction: (blueprint: IEventBlueprintDTO) => Promise<IEventBlueprintDTO>,
        @IoC.Inject private readonly globalViewModel: GlobalViewModel = IoC.Container.get(GlobalViewModel),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly attributesDAO: IAttributesDAO = IoC.Container.get(IAttributesDAO),
    ) {
        const initialData = {
            ...blueprint,
            eventAttributeIds: blueprint.eventAttributeIds || []
        };
        this.formDataCtrl = OrderBookingEventBlueprintsFormVM.createFormVM(initialData);
        this.currentBlueprint = blueprint;
        this.initFormVM();
    }

    @mobx.action
    public async initFormVM(): Promise<void> {
        if (this.accountId && this.blueprint?.blueprintId) {
            this.startLoading();
            try {
                const blueprint = await this.blueprintsDAO.getBlueprint<IBlueprintDTO>({
                    accountId: this.accountId,
                    blueprintId: this.blueprint.blueprintId
                }) as IEventBlueprintDTO;
                mobx.runInAction(() => {
                    if (blueprint?.accountId) {
                        this.currentBlueprint = blueprint;
                        const initialData = {
                            ...blueprint,
                            eventAttributeIds: blueprint.eventAttributeIds || []
                        };
                        this.formDataCtrl = OrderBookingEventBlueprintsFormVM.createFormVM(initialData);
                    }
                });
            } catch (error) {
                handleApiException(error);
            } finally {
                this.stopLoading();
            }
        }
        if (hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)) {
            await this.loadEventAttributes();
        } else {
            mobx.runInAction(() => {
                this.eventAttributes = [];
            });
        }
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get isSaving(): boolean {
        return this.saving;
    }

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }
        return formFields;
    };

    public getSelectedBlueprintTemplateDetails(): IBlueprintItemDetails | null {
        return this.currentBlueprint
            ? convertBlueprintTemplateIntoBlueprintDetails(
                this.currentBlueprint.type,
                this.currentBlueprint,
                this.globalViewModel.getTimezonesVM().getTimezonesList() || [],
                [],
                []
            )
            : null;
    }
    public get formVM(): AbstractFormViewModelController {
        return this.formDataCtrl;
    }

    private toDTO(): IEventBlueprintDTO {
        const dto = { ...this.currentBlueprint } as IEventBlueprintDTO;
        dto.publicName = (this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.PUBLIC_NAME) as string).trim();
        dto.publicDescription = (this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.PUBLIC_DESCRIPTION) as string).trim();
        dto.participantLinkType = (this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.PARTICIPANT_LINK_TYPE) as string).trim();
        dto.orderBookingEnabled = true;

        const eventAttributeIds = this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.EVENT_ATTRIBUTE_IDS);
        let sanitizedIds: number[] = [];
        if (Array.isArray(eventAttributeIds)) {
            sanitizedIds = eventAttributeIds
                .map(id => Number(id))
                .filter(id => !isNaN(id));
        }
        dto.eventAttributeIds = sanitizedIds;

        return dto;
    }

    @mobx.action
    public submitForm = async (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            return false;
        } else {
            this.startSaving();
            const result = await this.formSubmitAction(this.toDTO());
            this.stopSaving();
            return !!result;
        }
    };

    @mobx.action
    private startLoading = (): void => {
        this.loading = true;
    };

    @mobx.action
    private stopLoading = (): void => {
        this.loading = false;
    };

    @mobx.action
    private startSaving = (): void => {
        this.saving = true;
    };

    @mobx.action
    private stopSaving = (): void => {
        this.saving = false;
    };

    @mobx.action
    public async loadEventAttributes(): Promise<void> {
        try {
            const requestParams: { accountId: number } & ISortRequestParams<TGetEventAttributesSortKeys> = {
                accountId: this.accountId,
                sortField: 'createdAt',
                sortOrder: SortOrder.Desc
            };
            const response = await this.attributesDAO.getEventAttributesList(requestParams);
            mobx.runInAction(() => {
                this.eventAttributes = response.content.filter(item => item.active);
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    @mobx.action.bound
    public setEventAttributes(eventAttributes: IEventAttributeDetailsDTO[]): void {
        const eventAttributeIds = eventAttributes.map(attr => attr.eventAttributeId);
        this.formDataCtrl.setValue(OrderBookingEventBlueprintFieldKey.EVENT_ATTRIBUTE_IDS, eventAttributeIds);
    }

    @mobx.action.bound
    public removeEventAttribute(eventAttribute: IEventAttributeDetailsDTO): void {
        const currentIds = this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.EVENT_ATTRIBUTE_IDS) as number[] || [];
        const updatedIds = currentIds.filter(id => id !== eventAttribute.eventAttributeId);
        this.formDataCtrl.setValue(OrderBookingEventBlueprintFieldKey.EVENT_ATTRIBUTE_IDS, updatedIds);
    }

    public getSelectedEventAttributes(): IEventAttributeDetailsDTO[] {
        const eventAttributeIds = this.formDataCtrl.getValue(OrderBookingEventBlueprintFieldKey.EVENT_ATTRIBUTE_IDS) as number[] || [];
        if (!Array.isArray(eventAttributeIds) || eventAttributeIds.length === 0) {
            return [];
        }

        const attributes = this.eventAttributes || [];
        return attributes.filter(attr => eventAttributeIds.includes(attr.eventAttributeId));
    }

    public getAvailableEventAttributes(): IEventAttributeDetailsDTO[] {
        return this.eventAttributes || [];
    }
}
